import { InputNumberLocalized } from '@components'

import { Button, Divider, Flex, Icon, Radio, RadioGroup, Space, Spacing, Text } from '@billy/design-system'
import { ChangeEvent, Fragment, ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useBankAccountByBookkeepingAccountId } from '@features/bankAccounts/hooks/useBankAccountByBookkeepingAccountId'
import { TransactionTypeFilter } from '@features/bankTransactions/enums/transactionTypeFilter'

import { useDebouncedCallback } from '../../../../../../../../../../hooks/useDebouncedCallback'
import { useSandboxBankTransactionsFilters } from '../../../../../../hooks/useSandboxBankTransactionsFilters'
import * as Styled from './styles'

const DEBOUNCE_DELAY = 500

export const BankTransactionsFiltersPanel = (): ReactElement => {
  const { t } = useTranslation()
  const [filters, setFilters] = useSandboxBankTransactionsFilters()
  const { currency } = useBankAccountByBookkeepingAccountId(filters.accountId)

  const handleMinAmountChange = useDebouncedCallback((value: number) => {
    setFilters({ minAmount: value || undefined })
  }, DEBOUNCE_DELAY)

  const handleMaxAmountChange = useDebouncedCallback((value: number) => {
    setFilters({ maxAmount: value || undefined })
  }, DEBOUNCE_DELAY)

  const amountFields = useMemo(
    () => [
      { key: 'min', placeholder: '0', value: filters.minAmount, onChange: handleMinAmountChange },
      { key: 'max', placeholder: '1000000', value: filters.maxAmount, onChange: handleMaxAmountChange },
    ],
    [filters.minAmount, filters.maxAmount, handleMinAmountChange, handleMaxAmountChange],
  )

  const handleTransactionTypeChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      if (value === TransactionTypeFilter.Credit || value === TransactionTypeFilter.Debit) {
        setFilters({ transactionTypeFilter: value })
      }
    },
    [setFilters],
  )

  const handleClearAll = useCallback(() => {
    setFilters({
      transactionTypeFilter: undefined,
      minAmount: undefined,
      maxAmount: undefined,
    })
  }, [setFilters])

  return (
    <Styled.Panel>
      <Text colorVariant="discreet" weight="semiBold">
        {t('bank_transactions.filter.transaction_type')}
      </Text>
      <RadioGroup
        key={`transaction-type-${filters.transactionTypeFilter ?? ''}`}
        name="transactionTypeFilter"
        value={filters.transactionTypeFilter}
        onChange={handleTransactionTypeChange}
        direction="column"
        spacing={Spacing.S20}
      >
        <Radio value={TransactionTypeFilter.Debit}>
          {t('bank_transactions.filter.incoming')} <Icon icon="arrowDownLeft" />
        </Radio>
        <Radio value={TransactionTypeFilter.Credit}>
          {t('bank_transactions.filter.outgoing')} <Icon icon="arrowUpRight" />
        </Radio>
      </RadioGroup>
      <Space size="s8" />
      <Text colorVariant="discreet" weight="semiBold">
        {t('bank_transactions.filter.amount')}
      </Text>
      <Flex alignItems="center" gap={Spacing.S8}>
        {amountFields.map(({ key, placeholder, value, onChange }, index) => (
          <Fragment key={key}>
            {index > 0 && <Text colorVariant="secondary">{t('bank_transactions.filter.amount_separator')}</Text>}
            <InputNumberLocalized
              key={`${key}-${value === undefined}`}
              size="sm"
              min={0}
              isFallbackToZero={false}
              placeholder={placeholder}
              suffix={currency}
              value={value}
              onChange={onChange}
            />
          </Fragment>
        ))}
      </Flex>
      <Styled.FullWidthDivider>
        <Divider />
      </Styled.FullWidthDivider>
      <Flex justifyContent="flex-start">
        <Button variant="text" size="sm" icon="x" onClick={handleClearAll}>
          {t('bank_transactions.filter.clear_all')}
        </Button>
      </Flex>
    </Styled.Panel>
  )
}
